import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  Bot, 
  Loader2, 
  History,
  FileText,
  Sparkles
} from 'lucide-react';
import { generateAiScribeSummary, getScribeHistory } from '@/services/api';
import { toast } from 'sonner';

export default function AiScribePage() {
  const [notes, setNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await getScribeHistory();
      setHistory(data);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!notes.trim()) {
      toast.error('Please enter consultation notes');
      return;
    }

    setLoading(true);
    setSummary('');

    try {
      const result = await generateAiScribeSummary(notes);
      setSummary(result.summary);
      toast.success('Summary generated successfully!');
      await loadHistory();
    } catch (error) {
      console.error('Generate summary error:', error);
      toast.error('Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ai-scribe-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            AI Medical Scribe
          </h1>
          <p className="text-slate-600 mt-1">Transform consultation notes into structured medical summaries</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileText className="w-5 h-5 text-blue-900" />
                Consultation Notes
              </CardTitle>
              <CardDescription>
                Enter or paste your raw consultation notes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Patient presented with symptoms of... Doctor observed... Treatment plan includes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={12}
                className="resize-none"
                data-testid="scribe-notes-input"
              />
              <Button
                className="w-full bg-blue-900 hover:bg-blue-800 text-white rounded-lg py-5 btn-active"
                onClick={handleGenerate}
                disabled={loading || !notes.trim()}
                data-testid="generate-summary-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating Summary...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate AI Summary
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Output Section */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Bot className="w-5 h-5 text-blue-900" />
                AI Generated Summary
              </CardTitle>
              <CardDescription>
                Structured medical summary from your notes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary ? (
                <div 
                  className="p-4 bg-green-50 border border-green-200 rounded-xl animate-fade-in"
                  data-testid="scribe-summary-output"
                >
                  <pre className="whitespace-pre-wrap text-slate-800 text-sm leading-relaxed font-sans">
                    {summary}
                  </pre>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-center p-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                  <div>
                    <Bot className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Enter consultation notes and click generate</p>
                    <p className="text-sm text-slate-400 mt-1">AI will create a structured medical summary</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* History Section */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <History className="w-5 h-5 text-blue-900" />
              Recent Summaries
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingHistory ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton h-24 rounded-lg" />
                ))}
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <History className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No summaries generated yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.slice(0, 5).map((item, index) => (
                  <div 
                    key={item.id} 
                    className="p-4 bg-slate-50 rounded-xl border border-slate-200 animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-500">
                        {new Date(item.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-slate-700 text-sm line-clamp-3">{item.summary}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
